import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import api, { uploadFile } from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit3, Trash2, Tag, X, Upload, Globe, Instagram, Youtube, Facebook, Music2, Image } from 'lucide-react';

export function LabelModal({ label, userId, onClose, onSaved }) {
  const [form, setForm] = useState(label || { name: '', description: '', logo_path: '', link_website: '', link_instagram: '', link_facebook: '', link_youtube: '', link_tiktok: '' });
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(label?.logo_path ? `/uploads/artwork/${userId}/${label.logo_path}` : null);
  const [uploading, setUploading] = useState(false);

  const handleLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const { data } = await uploadFile('/uploads/artwork', f, 'artwork');
      setForm(p => ({ ...p, logo_path: data.file.filename }));
      setLogoPreview(URL.createObjectURL(f));
      toast.success('Logo caricato');
    } catch (err) { toast.error('Errore upload'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error('Nome label obbligatorio');
    setSaving(true);
    try {
      let savedLabel;
      if (label?.id) {
        await api.put(`/labels/${label.id}`, form);
        savedLabel = { ...label, ...form };
        toast.success('Label aggiornata!');
      } else {
        const { data } = await api.post('/labels', form);
        savedLabel = data.label || { ...form, id: data.id };
        toast.success('Label creata!');
      }
      onSaved(savedLabel);
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">{label?.id ? 'Modifica Label' : 'Nuova Label'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl bg-gray-100 border-2 border-dashed overflow-hidden flex-shrink-0 flex items-center justify-center">
              {uploading ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pn-red" /> : logoPreview ? <img src={logoPreview} alt="" className="w-full h-full object-cover" /> : <Image size={24} className="text-gray-300" />}
            </div>
            <div>
              <input type="file" accept="image/*" className="hidden" id="label-logo" onChange={handleLogo} />
              <label htmlFor="label-logo" className="btn-secondary text-xs cursor-pointer"><Upload size={14} className="inline mr-1" />Logo Label</label>
              <p className="text-[10px] text-gray-400 mt-1">JPG/PNG, consigliato 500×500px, quadrata</p>
              {logoPreview && <button onClick={() => { setForm(p => ({ ...p, logo_path: '' })); setLogoPreview(null); }} className="text-xs text-red-400 hover:underline ml-2">Rimuovi</button>}
            </div>
          </div>
          <div><label className="block text-sm font-medium mb-1">Nome Label *</label><input className="input-field" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} autoFocus /></div>
          <div><label className="block text-sm font-medium mb-1">Descrizione</label><textarea className="input-field h-20 resize-y" value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Descrivi la tua label..." /></div>
          <div className="border-t pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Link & Social</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2"><Globe size={14} className="text-gray-400 flex-shrink-0" /><input className="input-field" value={form.link_website || ''} onChange={e => setForm(p => ({ ...p, link_website: e.target.value }))} placeholder="https://tuosito.com" /></div>
              <div className="flex items-center gap-2"><Instagram size={14} className="text-gray-400 flex-shrink-0" /><input className="input-field" value={form.link_instagram || ''} onChange={e => setForm(p => ({ ...p, link_instagram: e.target.value }))} placeholder="https://instagram.com/tualabel" /></div>
              <div className="flex items-center gap-2"><Facebook size={14} className="text-gray-400 flex-shrink-0" /><input className="input-field" value={form.link_facebook || ''} onChange={e => setForm(p => ({ ...p, link_facebook: e.target.value }))} placeholder="https://facebook.com/tualabel" /></div>
              <div className="flex items-center gap-2"><Youtube size={14} className="text-gray-400 flex-shrink-0" /><input className="input-field" value={form.link_youtube || ''} onChange={e => setForm(p => ({ ...p, link_youtube: e.target.value }))} placeholder="https://youtube.com/@tualabel" /></div>
              <div className="flex items-center gap-2"><Music2 size={14} className="text-gray-400 flex-shrink-0" /><input className="input-field" value={form.link_tiktok || ''} onChange={e => setForm(p => ({ ...p, link_tiktok: e.target.value }))} placeholder="https://tiktok.com/@tualabel" /></div>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5"><button onClick={onClose} className="btn-secondary flex-1">Annulla</button><button onClick={save} disabled={saving} className="btn-primary flex-1">{saving ? 'Salvataggio...' : label?.id ? 'Salva Modifiche' : 'Crea Label'}</button></div>
      </div>
    </div>
  );
}

export default function LabelsPage() {
  const { user } = useAuth();
  const lang = user?.language || 'it';
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, label: null });

  const load = async () => { try { const { data } = await api.get('/labels'); setLabels(data.labels); } catch (e) {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm(lang === 'it' ? 'Eliminare questa label?' : 'Delete this label?')) return;
    try { await api.delete(`/labels/${id}`); toast.success('Label eliminata'); load(); } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  return (
    <div>
      {modal.open && <LabelModal label={modal.label} userId={user?.id} onClose={() => setModal({ open: false, label: null })} onSaved={() => { setModal({ open: false, label: null }); load(); }} />}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-pn-dark">{lang === 'it' ? 'Le Mie Label' : 'My Labels'}</h1>
        <button onClick={() => setModal({ open: true, label: null })} className="btn-primary inline-flex items-center gap-2"><Plus size={18} />{lang === 'it' ? 'Nuova Label' : 'New Label'}</button>
      </div>
      {loading ? <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pn-red" /></div> : labels.length === 0 ? (
        <div className="text-center py-16"><Tag size={48} className="mx-auto text-gray-300 mb-4" /><p className="text-gray-400">{lang === 'it' ? 'Nessuna label. Creane una!' : 'No labels yet.'}</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {labels.map(l => (
            <div key={l.id} className="card p-5 hover:shadow-md transition">
              <div className="flex items-start gap-3">
                {l.logo_path ? <img src={`/uploads/artwork/${user?.id}/${l.logo_path}`} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-pn-dark flex items-center justify-center"><Tag size={20} className="text-white" /></div>}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-pn-dark">{l.name}</h3>
                  {l.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{l.description}</p>}
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                <button onClick={() => setModal({ open: true, label: l })} className="text-xs text-blue-500 hover:underline flex items-center gap-1"><Edit3 size={12} />{lang === 'it' ? 'Modifica' : 'Edit'}</button>
                <button onClick={() => del(l.id)} className="text-xs text-red-400 hover:underline flex items-center gap-1"><Trash2 size={12} />{lang === 'it' ? 'Elimina' : 'Delete'}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
